import React, { useState, useEffect } from "react";
import { useEscapeClose } from "../../../../utils/SamuiUtils";
import {
  useModalKeyboardNavigation,
  getKeyboardInstructions,
} from "../../../../utils/ModalKeyboardNavigation";
import api from "../../../../utils/axios";

export default function PackageModal({ isOpen, onClose, onSelect, selectedIds = [] }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const compId = localStorage.getItem("company") || 1;

  useEscapeClose(isOpen, onClose);

  useModalKeyboardNavigation(
    isOpen,
    filteredList,
    selectedRowIndex,
    setSelectedRowIndex,
    null,
    onClose,
    {
      customEnterHandler: () => handleConfirm(),
      customSpaceHandler: () =>
        handleCheckboxChange(filteredList[selectedRowIndex]),
      enableSpace: true,
      includeHighlightedOnEnter: false,
      selectedItems: selectedRows,
    }
  );

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([
        api.get(`/get-packages/${compId}`),
        api.get(`/get-items/${compId}`),
        api.get(`/get-services?Comp_Id=${compId}`),
      ])
        .then(([packRes, itemRes, svcRes]) => {
          const packagesData =
            packRes.data.status === "OK" && Array.isArray(packRes.data.result)
              ? packRes.data.result.map((p) => ({ ...p, Item_Img: p.Pack_Img || null }))
              : [];

          const itemsData =
            itemRes.data.status === "OK" && Array.isArray(itemRes.data.result)
              ? itemRes.data.result
              : [];

          const servicesData =
            svcRes.data.status === "OK" && Array.isArray(svcRes.data.result)
              ? svcRes.data.result.filter((s) => s.Service_Status === "Y")
              : [];

          setPackages(packagesData);
          setItems(itemsData);
          setServices(servicesData);
        })
        .catch((err) => console.error("โหลดข้อมูล error:", err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, compId]);

  useEffect(() => {
    if (isOpen) {
      setActiveCategory("package");
    } else {
      setSearchTerm("");
      setFilteredList([]);
      setSelectedRows([]);
      setSelectedRowIndex(0);
    }
  }, [isOpen]);

  function createImageUrlFromBuffer(buffer) {
    try {
      if (buffer && buffer.data && Array.isArray(buffer.data)) {
        const blob = new Blob([Uint8Array.from(buffer.data)], { type: "image/jpeg" });
        return URL.createObjectURL(blob);
      }
    } catch (err) {
      console.error("❌ Error creating image URL:", err);
    }
    return null;
  }

  useEffect(() => {
    const q = searchTerm.toLowerCase();
    let list = [];
    if (activeCategory === "item") {
      list = items.filter(
        (i) =>
          i.Item_Name?.toLowerCase().includes(q) ||
          i.Item_Code?.toLowerCase().includes(q)
      );
    } else {
      // "บริการ" tab = packages + services รวมกัน
      const filteredPackages = packages
        .filter((p) => p.Pack_Name?.toLowerCase().includes(q))
        .map((p) => ({ ...p, _src: "package" }));
      const filteredServices = services
        .filter((s) => s.Service_Name?.toLowerCase().includes(q) || s.Service_Code?.toLowerCase().includes(q))
        .map((s) => ({ ...s, _src: "service" }));
      list = [...filteredPackages, ...filteredServices];
    }
    setFilteredList(list);
    setSelectedRowIndex(0);
  }, [searchTerm, activeCategory, items, packages]);

  const getRowId = (row) => {
    if (activeCategory === "item") return row.Item_Id;
    // "บริการ" tab: ใช้ _src แยก package vs service
    if (row._src === "service") return `svc_${row.Service_Id}`;
    return row.Pack_Id;
  };

  const handleCheckboxChange = (row) => {
    if (!row) return;
    const id = getRowId(row);
    setSelectedRows((prev) => {
      const exists = prev.some((r) => getRowId(r) === id);
      return exists ? prev.filter((r) => getRowId(r) !== id) : [...prev, row];
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedRows([...filteredList]);
    else setSelectedRows([]);
  };

  const handleConfirm = () => {
    const mapped = selectedRows.map((r) => {
      const data = { ...r };

      if (!data.Item_Img && data.Pack_Img) {
        data.Item_Img = data.Pack_Img;
      }

      if (data.Item_Img?.data && Array.isArray(data.Item_Img.data)) {
        try {
          const blob = new Blob([Uint8Array.from(data.Item_Img.data)], {
            type: "image/jpeg",
          });
          data.Item_Img_Url = URL.createObjectURL(blob);
        } catch { }
      } else {
        data.Item_Img_Url = null;
      }
      return data;
    });

    const payload = {
      type: activeCategory,
      packages: activeCategory === "package" ? mapped.filter(r => r._src !== "service") : [],
      services: activeCategory === "package" ? mapped.filter(r => r._src === "service") : [],
      items: activeCategory === "item" ? mapped : [],
    };

    onSelect(payload);
    onClose();
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .package-table-header th,
      .package-table-header span {
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);


  if (!isOpen) return null;

  return (
    <>
      <div
        className={`modal fade ${isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content border-0 shadow-lg">

            {/*  Header */}
            <div className="modal-header" style={{ backgroundColor: "#E13F57", color: "white" }}>
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                เลือกหมวดสินค้า / บริการ
              </h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">

              <div className="form-group mb-3">
                <div
                  className="d-flex align-items-center justify-content-start"
                  style={{
                    gap: "90px",     // ระยะระหว่างกลุ่ม radio และช่องค้นหา
                    flexWrap: "nowrap",
                  }}
                >
                  {/* 🔹 กลุ่ม Radio */}
                  <div className="d-flex align-items-center" style={{ gap: "16px" }}>
                    {/*  บริการ */}
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        margin: 0,
                        padding: 0,
                        width: "auto",
                        height: "auto",
                        lineHeight: 1,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="category"
                        value="package"
                        checked={activeCategory === "package"}
                        onChange={() => setActiveCategory("package")}
                        style={{
                          width: "15px",
                          height: "15px",
                          accentColor: "#E13F57",
                          margin: 0,
                          padding: 0,
                        }}
                      />
                      <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                        บริการ
                      </span>
                    </label>

                    {/*  สินค้า */}
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        margin: 0,
                        padding: 10,
                        width: "auto",
                        height: "auto",
                        lineHeight: 1,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="category"
                        value="item"
                        checked={activeCategory === "item"}
                        onChange={() => setActiveCategory("item")}
                        style={{
                          width: "15px",
                          height: "15px",
                          accentColor: "#E13F57",
                          margin: 0,
                          padding: 0,
                        }}
                      />
                      <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                        สินค้า
                      </span>
                    </label>
                  </div>

                  {/* 🔹 ช่องค้นหา */}
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ค้นหา..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: "500px",
                      height: "36px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>



              {/*  ตาราง */}
              <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table className="table table-striped table-hover mb-0" style={{ fontSize: "14px" }}>

                  {/*  หัวตารางสีเดียวกัน no matter item/package */}
                  <thead
                    className="package-table-header"
                    style={{
                      backgroundColor: "#E13F57",
                      position: "sticky",
                      top: 0,
                      zIndex: 2
                    }}
                  >
                    <tr>
                      <th className="text-center" style={{ width: "6%" }}>
                        <input
                          type="checkbox"
                          style={{ width: "14px", height: "14px" }}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          checked={filteredList.length > 0 && selectedRows.length === filteredList.length}
                        />
                      </th>

                      <th className="text-center" style={{ width: "15%" }}>
                        รูปสินค้า
                      </th>

                      <th className="text-left">ชื่อสินค้า / บริการ</th>

                      <th className="text-right" style={{ width: "18%" }}>
                        ราคา
                      </th>
                    </tr>
                  </thead>



                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">กำลังโหลด...</td>
                      </tr>
                    ) : filteredList.length ? (
                      filteredList.map((row) => {
                        const id = getRowId(row);
                        const name =
                          activeCategory === "item" ? row.Item_Name
                          : row._src === "service" ? row.Service_Name
                          : row.Pack_Name;
                        const price =
                          activeCategory === "item" ? row.Item_Price
                          : row._src === "service" ? 0
                          : row.Pack_Price;
                        const selected = selectedRows.some((r) => getRowId(r) === id);
                        const alreadyExists = selectedIds.includes(id);

                        const imgSrc =
                          activeCategory === "item"
                            ? createImageUrlFromBuffer(row.Item_Img)
                            : row._src === "service"
                            ? null
                            : createImageUrlFromBuffer(row.Pack_Img);

                        return (
                          <tr
                            key={id}
                            onClick={() => !alreadyExists && handleCheckboxChange(row)}
                            style={{
                              cursor: alreadyExists ? "not-allowed" : "pointer",
                              backgroundColor: selected ? "rgba(225,63,87,0.12)" : "transparent",
                            }}
                          >
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={selected || false}
                                disabled={alreadyExists}
                                onChange={() => handleCheckboxChange(row)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: "14px", height: "14px" }}
                              />
                            </td>

                            <td
                              className="text-center align-middle"
                              style={{
                                verticalAlign: "middle",
                                textAlign: "center",
                                height: imgSrc ? "75px" : "auto",
                              }}
                            >
                              {imgSrc ? (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                    height: "100%",
                                  }}
                                >
                                  <img
                                    src={imgSrc}
                                    alt={name}
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "cover",
                                      borderRadius: "6px",
                                      opacity: alreadyExists ? 0.4 : 1,
                                    }}
                                  />
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>


                            <td className="align-middle" style={{ opacity: alreadyExists ? 0.5 : 1 }}>
                              {name}
                              {alreadyExists && <small className="text-danger ml-2">(มีอยู่แล้ว)</small>}
                            </td>

                            <td className="text-right align-middle">{Number(price || 0).toLocaleString()}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-muted">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            </div>

            {/*  Footer */}
            <div className="modal-footer">
              <div className="text-left text-muted mr-auto" style={{ fontSize: "12px" }}>
                {getKeyboardInstructions("สินค้า / แพ็กเกจ", true)}
              </div>

              <button
                className="btn btn-primary"
                style={{ backgroundColor: "#E13F57", borderColor: "#E13F57" }}
                onClick={handleConfirm}
              >
                ยืนยัน (Enter)
              </button>

              <button
                className="btn"
                style={{
                  backgroundColor: "#E13F57",
                  borderColor: "#E13F57",
                  color: "white",
                }}
                onClick={onClose}
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      </div>

      {isOpen && <div className="modal-backdrop fade show"></div>}
    </>
  );
}
